import React, { useRef, useState } from "react";
import { CloudUpload, FileText, X, Loader2 } from "lucide-react";

export default function SleekUploadTile({
    title,
    sub,
    button = "Select File",
    accept = ".pdf,.jpg,.jpeg,.png",
    multiple = false,
    onFiles,
    onRemove, // (fileId, bucketKey) => void
    existingFiles = [], // Array of { name, file_name, file_url, id }
    uploadingFiles = [], // Array of { id, name, progress } - files currently being uploaded
    maxSizeMB = 10, // per-file size limit; set to null/0 to disable
}) {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState("");

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const MAX_BYTES = maxSizeMB ? maxSizeMB * 1024 * 1024 : null;

    const processFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        if (!MAX_BYTES) {
            setError("");
            onFiles?.(files);
            return;
        }

        const valid = [];
        const tooBig = [];

        files.forEach((file) => {
            if (file.size > MAX_BYTES) {
                tooBig.push(file);
            } else {
                valid.push(file);
            }
        });

        if (tooBig.length > 0) {
            if (tooBig.length === 1) {
                const f = tooBig[0];
                setError(
                    `File "${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)}MB. Maximum allowed is ${maxSizeMB}MB.`
                );
            } else {
                setError(
                    `${tooBig.length} files exceed the ${maxSizeMB}MB limit and were not added.`
                );
            }
        } else {
            setError("");
        }

        if (valid.length > 0) {
            onFiles?.(valid);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    // Helper to get file extension icon color (matching lender portal)
    const getFileIconColor = (fileName) => {
        const ext = fileName?.split(".").pop()?.toLowerCase() || "";
        if (["xlsx", "xls"].includes(ext)) return "text-emerald-600";
        if (ext === "csv") return "text-blue-600";
        if (ext === "pdf") return "text-rose-600";
        if (["jpg", "jpeg", "png"].includes(ext)) return "text-amber-600";
        return "text-slate-600";
    };

    // Check if file is an image
    const isImageFile = (fileName) => {
        const ext = fileName?.split(".").pop()?.toLowerCase() || "";
        return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    };

    // Get file URL for preview
    const getFileUrl = (file) => {
        if (file.file_url) return file.file_url;
        if (file.url) return file.url;
        // If it's a relative path, try to construct full URL
        if (file.name && file.name.startsWith("/")) return file.name;
        return null;
    };

    const hasFiles = existingFiles && existingFiles.length > 0;

    return (
        <div className="flex flex-col h-full">
            <div
                className={`group relative flex flex-col rounded-2xl border-2 border-dashed transition-all duration-200 p-4 md:p-6 cursor-pointer shadow-sm hover:shadow-md min-h-[200px] ${isDragging
                    ? "border-sky-500 bg-sky-50 shadow-sky-100"
                    : "border-slate-200 bg-white hover:border-sky-400 hover:bg-slate-50/50"
                    }`}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleClick();
                    }
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept={accept}
                    multiple={multiple}
                    onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                            processFiles(e.target.files);
                            e.target.value = "";
                        }
                    }}
                />

                {/* Upload Progress Bars - Always show when files are uploading */}
                {uploadingFiles && uploadingFiles.length > 0 && (
                    <div className="w-full mb-3 space-y-2">
                        {uploadingFiles.map((uploadingFile) => (
                            <div
                                key={uploadingFile.id}
                                className="rounded-lg border border-sky-300 bg-sky-50 p-3 shadow-sm"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <Loader2 className="h-4 w-4 text-sky-600 animate-spin flex-shrink-0" />
                                        <span className="text-xs font-medium text-slate-700 truncate" title={uploadingFile.name}>
                                            {uploadingFile.name}
                                        </span>
                                    </div>
                                    <span className="text-xs font-semibold text-sky-600 ml-2 flex-shrink-0">
                                        {uploadingFile.progress || 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-sky-200 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-sky-600 h-full rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${uploadingFile.progress || 0}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Show existing files inside the tile */}
                {hasFiles ? (
                    <div className="w-full space-y-3">
                        <div className="text-xs font-bold text-slate-700 mb-2">{title}</div>
                        {sub && <div className="text-[10px] font-medium text-slate-500 leading-tight mb-3">{sub}</div>}

                        {/* File Grid - Images show preview, others show icon */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {existingFiles.map((file, idx) => {
                                const fileName = file.file_name || file.name || "";
                                const fileUrl = getFileUrl(file);
                                const isImage = isImageFile(fileName);

                                return (
                                    <div
                                        key={file.id || idx}
                                        className="relative group/file rounded-lg border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {/* Badges */}
                                        <div className="absolute top-1 left-1 flex gap-1 z-10">
                                            {file.isExisting && (
                                                <span className="px-1.5 py-0.5 rounded bg-slate-800/80 text-white text-[8px] font-bold uppercase tracking-wider backdrop-blur-sm shadow-sm group-hover/file:bg-slate-900 transition-colors">
                                                    Record
                                                </span>
                                            )}
                                            {file.isNew && (
                                                <span className="px-1.5 py-0.5 rounded bg-sky-500/90 text-white text-[8px] font-bold uppercase tracking-wider backdrop-blur-sm shadow-sm group-hover/file:bg-sky-600 transition-colors">
                                                    New
                                                </span>
                                            )}
                                        </div>

                                        {isImage && fileUrl ? (
                                            // Image Preview
                                            <div className="relative aspect-square">
                                                <img
                                                    src={fileUrl}
                                                    alt={fileName}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        // Fallback to icon if image fails to load
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="hidden absolute inset-0 items-center justify-center bg-slate-50">
                                                    <FileText className={`h-6 w-6 ${getFileIconColor(fileName)}`} />
                                                </div>
                                                {/* Overlay on hover */}
                                                <div className="absolute inset-0 bg-black/0 group-hover/file:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover/file:opacity-100">
                                                    <button
                                                        type="button"
                                                        title="Remove file"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemove?.(file.id || file.name);
                                                        }}
                                                        className="p-2 rounded-lg bg-white/90 hover:bg-white text-rose-600 shadow-lg"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // File Icon for non-images
                                            <div className="relative aspect-square flex flex-col items-center justify-center p-3 bg-slate-50">
                                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-slate-200 mb-2 ${getFileIconColor(fileName)}`}>
                                                    <FileText className="h-5 w-5" />
                                                </div>
                                                <div className="text-[10px] font-semibold text-slate-700 text-center truncate w-full px-1" title={fileName}>
                                                    {fileName}
                                                </div>
                                                {/* Remove button */}
                                                <button
                                                    type="button"
                                                    title="Remove file"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemove?.(file.id || file.name);
                                                    }}
                                                    className="absolute top-1 right-1 p-1 rounded-lg bg-white/90 hover:bg-rose-50 text-slate-400 hover:text-rose-600 shadow-sm opacity-0 group-hover/file:opacity-100 transition-opacity"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        )}
                                        {/* File name tooltip for images */}
                                        {isImage && fileUrl && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-medium px-2 py-1 truncate opacity-0 group-hover/file:opacity-100 transition-opacity">
                                                {fileName}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Add more files button */}
                        <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-bold text-slate-600 uppercase tracking-wider hover:bg-sky-100 hover:text-sky-700 transition-colors">
                                <CloudUpload className="h-3 w-3" />
                                {button}
                            </div>
                        </div>
                    </div>
                ) : (
                    // Empty state - show upload prompt
                    <div className="w-full flex flex-col items-center justify-center">
                        {uploadingFiles && uploadingFiles.length > 0 ? (
                            // When uploading, show title and sub above progress bars
                            <>
                                <div className="text-xs font-bold text-slate-700 mb-2 w-full text-left">{title}</div>
                                {sub && <div className="text-[10px] font-medium text-slate-500 leading-tight mb-3 w-full text-left">{sub}</div>}
                            </>
                        ) : (
                            // When not uploading, show centered upload prompt
                            <>
                                <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br transition-transform duration-200 group-hover:scale-110 ${isDragging
                                    ? "from-sky-500 to-blue-600 shadow-lg scale-110"
                                    : "from-sky-50 to-white border border-sky-100"
                                    }`}>
                                    <CloudUpload className={`h-7 w-7 ${isDragging ? "text-white" : "text-sky-500"}`} />
                                </div>

                                <div className="text-sm font-bold text-slate-900 mb-1 text-center">{title}</div>
                                {sub && <div className="text-[11px] font-medium text-slate-500 leading-tight max-w-[200px] mb-4 mx-auto text-center">{sub}</div>}

                                <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-600 uppercase tracking-wider group-hover:bg-sky-100 group-hover:text-sky-700 transition-colors">
                                    {button}
                                </div>
                            </>
                        )}
                    </div>
                )
                }
            </div >
            {error && (
                <div className="mt-1.5 text-[11px] text-rose-600 font-medium">
                    {error}
                </div>
            )}
        </div >
    );
}
